import { useEffect, useMemo, useState } from 'react';
import type { ElementType, ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowUpRight,
  BookOpenCheck,
  BrainCircuit,
  Cable,
  CheckCircle2,
  ClipboardCheck,
  CloudCog,
  FileDown,
  FileText,
  Gauge,
  Headphones,
  ListChecks,
  MessageSquareText,
  Network,
  PackageCheck,
  PhoneCall,
  RadioTower,
  ShieldCheck,
  Sparkles,
  Target,
  Users,
  X,
} from 'lucide-react';

interface FormationTelephonieBZTelecomModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type TabId = 'overview' | 'telephony' | 'cabling' | 'wiki' | 'offer' | 'products';

const SOURCE_FILE = 'PRIsME_Formation_Telephonie_BZ_Telecom_2026-08-03.pdf';
const SOURCE_URL = `/documents/bz-telecom/${SOURCE_FILE}`;

const tabs: { id: TabId; label: string; icon: ElementType }[] = [
  { id: 'overview', label: 'Vue d’ensemble', icon: Target },
  { id: 'telephony', label: 'Vente téléphonie', icon: PhoneCall },
  { id: 'cabling', label: 'Câblage & fibre', icon: Cable },
  { id: 'wiki', label: 'Wiki & lexique', icon: BookOpenCheck },
  { id: 'offer', label: 'Offre BZ Telecom', icon: CloudCog },
  { id: 'products', label: 'Produits rattachés', icon: PackageCheck },
];

const telephonySalesSteps = [
  ['Qualifier le contexte', 'Utilisateurs, sites, numéros, postes, réception, files d’appel, horaires et contraintes actuelles.'],
  ['Identifier les douleurs', 'Coûts, pannes, appels manqués, transferts, télétravail, mobilité, rapports et administration.'],
  ['Valider le réseau', 'Internet, pare-feu, commutateurs, VLAN voix, PoE, qualité de service et redondance.'],
  ['Présenter la cible', 'Extensions, groupes d’appel, menus vocaux, messagerie, enregistrements et mobilité.'],
  ['Préparer la proposition', 'Forfaits, équipements, portabilité, installation, formation, support et transition.'],
  ['Planifier la mise en service', 'Collecte, tests, portabilité, formation des utilisateurs et suivi après activation.'],
];

const telephonyProducts = [
  'Lignes et utilisateurs de téléphonie hébergée',
  'Téléphones IP, bases sans-fil DECT et accessoires',
  'Menus vocaux, files d’appel, groupes de sonnerie et règles horaires',
  'Messagerie vocale vers courriel et options de mobilité',
  'Portabilité et configuration des appels entrants et sortants',
  'Installation, formation et support continu',
];

const networkChecks = [
  'Capacité et stabilité du lien Internet',
  'Pare-feu et règles nécessaires au trafic voix',
  'Commutateurs compatibles PoE',
  'VLAN voix et segmentation réseau',
  'QoS pour prioriser la voix',
  'Redondance Internet et scénario de continuité',
];

const cablingSteps = [
  ['Prise de besoin', 'Plans, nombre de prises, équipements, zones de travail, salle serveur et contraintes du bâtiment.'],
  ['Validation du site', 'Parcours, plafonds, conduits, distances, obstacles, normes, heures de travail et accès.'],
  ['Définition de la solution', 'Cuivre Cat6/Cat6A, fibre monomode ou multimode, panneaux, cabinets, racks et identification.'],
  ['Évaluation des risques', 'Distances maximales, environnement électrique, hauteur, perçage, interruption et sous-traitance.'],
  ['Soumission complète', 'Matériel, main-d’œuvre, certification, documentation, photos, plans et exclusions explicites.'],
  ['Livraison et fermeture', 'Installation, tests, étiquetage, certification, remise documentaire et validation client.'],
];

const cablingProducts = [
  'Câblage structuré cuivre Cat6 et Cat6A',
  'Fibre optique monomode ou multimode',
  'Panneaux de brassage, prises, keystones et cordons',
  'Racks, cabinets et gestionnaires de câbles',
  'Tests, certification, étiquetage et documentation',
  'Installation, déplacement, ajout et nettoyage du câblage existant',
];

const cablingRisks = [
  'Distance maximale et performance attendue',
  'Interférences et environnement électrique',
  'Travaux en hauteur, perçage et accès restreints',
  'Fenêtre de travaux et interruption des opérations',
  'Normes du bâtiment et recours à des sous-traitants',
  'Exclusions clairement nommées avant l’approbation',
];

const glossary = [
  ['Téléphonie hébergée', 'Système téléphonique hébergé dans le nuage et géré à distance plutôt que dans une centrale locale.'],
  ['VoIP', 'La voix circule sur Internet ou sur le réseau informatique plutôt que sur une ligne traditionnelle.'],
  ['PBX', 'Le cerveau du système : il gère extensions, transferts, menus et règles d’appel.'],
  ['Extension', 'Numéro interne d’un utilisateur, d’un poste ou d’un service.'],
  ['DID / numéro direct', 'Numéro public qui rejoint directement une personne, une équipe ou un service.'],
  ['Portabilité', 'Transfert des numéros actuels vers la nouvelle solution sans changer les numéros connus.'],
  ['Menu vocal / IVR', 'Accueil automatisé qui dirige l’appelant vers le bon service.'],
  ['File d’appel', 'Ligne d’attente qui distribue les appels aux employés selon des règles définies.'],
  ['Softphone', 'Application sur ordinateur ou mobile permettant d’appeler avec le numéro professionnel.'],
  ['PoE', 'Alimentation électrique fournie au téléphone IP directement par le câble réseau.'],
  ['QoS', 'Priorité réseau donnée à la voix pour limiter les coupures et les délais.'],
  ['SIP', 'Protocole utilisé par les systèmes VoIP pour établir et gérer les appels.'],
];

const hostedBenefits = [
  ['Flexibilité', 'Téléphone IP, ordinateur, téléphone intelligent ou tablette avec le même numéro professionnel.'],
  ['Évolutivité', 'Ajouter un utilisateur rapidement sans déployer une nouvelle centrale.'],
  ['Haute disponibilité', 'Architecture redondante pour maintenir les communications en cas d’incident.'],
  ['Coûts prévisibles', 'Modèle mensuel par utilisateur qui simplifie la planification budgétaire.'],
];

const popularFeatures = [
  'Standard automatique / IVR',
  'Files d’attente et groupes de sonnerie',
  'Messagerie vocale par courriel',
  'Applications mobiles et télétravail',
  'Enregistrement des appels',
  'Conférence et vidéoconférence',
  'Présence et transfert d’appel',
  'Règles horaires et jours fériés',
  'Statistiques, rapports et performance des agents',
];

const turnkeyServices = [
  'Analyse des besoins',
  'Configuration complète du système',
  'Portabilité des numéros existants',
  'Installation des téléphones IP',
  'Formation des utilisateurs',
  'Maintenance et mises à jour',
  'Support technique local',
  'Surveillance proactive de la plateforme',
];

const platformFeatures = [
  'IVR, files d’attente et groupes de sonnerie',
  'Boîtes vocales personnalisées et renvoi intelligent',
  'Téléphones IP, applications mobiles et softphones',
  'Conférences, présence et messagerie vocale par courriel',
  'Rapports détaillés et enregistrement des appels',
  'Intégration Microsoft Teams et applications d’affaires, selon les besoins',
];

const aiFeatures = [
  'Transcription automatique des appels et messages vocaux',
  'Résumés intelligents des conversations',
  'Évaluation de la qualité et des performances des agents',
  'Recherche par mots-clés dans les appels enregistrés',
  'Automatisation de tâches administratives ciblées',
];

const idealFor = [
  'Entreprises multisites',
  'Équipes en télétravail',
  'Centres de contact',
  'Entreprises de services',
  'Commerces et organismes publics',
  'Organisations en croissance',
];

const relatedProducts = [
  {
    title: 'Plateforme SMS centralisée',
    icon: MessageSquareText,
    summary: 'Une boîte de réception partagée pour gérer les textos d’entreprise en équipe, avec historique complet et continuité de service.',
    features: ['Numéro partagé', 'Réponses multiagents', 'Modèles et automatisations', 'Notifications en temps réel', 'Accès ordinateur et mobile', 'Intégration téléphonie ou CRM selon le besoin'],
  },
  {
    title: 'Rapports évolués de files d’attente',
    icon: Gauge,
    summary: 'Des tableaux de bord opérationnels pour améliorer le niveau de service, planifier les horaires et optimiser les ressources.',
    features: ['Appels reçus, répondus et abandonnés', 'Temps d’attente et de conversation', 'Traitement après appel', 'SLA et occupation', 'Performance par agent et équipe', 'Exports Excel, PDF et CSV'],
  },
  {
    title: 'EVA — intelligence des appels',
    icon: BrainCircuit,
    summary: 'Enregistrement sécurisé, recherche, transcription, résumé et évaluation afin de transformer les appels en information exploitable.',
    features: ['Conservation selon les politiques applicables', 'Recherche multicritère', 'Transcription et résumé', 'Évaluation automatisée', 'Recherche intelligente', 'Tableaux de bord qualité et tendances'],
  },
  {
    title: 'Calendrier d’urgence',
    icon: RadioTower,
    summary: 'Acheminement automatique des appels hors heures normales vers la bonne personne de garde, selon un calendrier prédéfini.',
    features: ['Horaires de garde', 'Routage par date et heure', 'Escalade automatique', 'Notifications appel, SMS ou courriel', 'Mise à jour en temps réel', 'Historique des interventions'],
  },
  {
    title: 'Transcription des messages vocaux',
    icon: Headphones,
    summary: 'Conversion automatique d’un message vocal en texte, avec fichier audio et résumé selon la configuration.',
    features: ['Lecture rapide et discrète', 'Recherche par mots-clés', 'Archivage simplifié', 'Gain de temps', 'Suivi client amélioré', 'Audio original conservé'],
  },
];

function SectionCard({ title, icon: Icon, children, tone = 'cyan' }: { title: string; icon: ElementType; children: ReactNode; tone?: 'cyan' | 'emerald' | 'violet' | 'amber' | 'slate' }) {
  const toneClass = {
    cyan: 'border-cyan-400/20 bg-cyan-500/10',
    emerald: 'border-emerald-400/20 bg-emerald-500/10',
    violet: 'border-violet-400/20 bg-violet-500/10',
    amber: 'border-amber-400/20 bg-amber-500/10',
    slate: 'border-white/10 bg-white/[0.055]',
  }[tone];
  return (
    <section className={`rounded-3xl border p-4 md:p-5 ${toneClass}`}>
      <h3 className="mb-3 flex items-center gap-2 text-base font-black text-white md:text-lg">
        <Icon className="h-5 w-5 shrink-0 text-cyan-200" />
        {title}
      </h3>
      <div className="text-sm leading-6 text-slate-200">{children}</div>
    </section>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item} className="flex gap-2">
          <CheckCircle2 className="mt-1 h-4 w-4 shrink-0 text-emerald-300" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function StepGrid({ steps }: { steps: string[][] }) {
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {steps.map(([title, body], index) => (
        <div key={title} className="rounded-2xl border border-white/10 bg-slate-950/45 p-4">
          <div className="mb-2 flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-cyan-400 text-xs font-black text-slate-950">{index + 1}</span>
            <h4 className="font-black text-white">{title}</h4>
          </div>
          <p className="text-xs leading-5 text-slate-300">{body}</p>
        </div>
      ))}
    </div>
  );
}

export default function FormationTelephonieBZTelecomModal({ isOpen, onClose }: FormationTelephonieBZTelecomModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const content = useMemo(() => {
    if (activeTab === 'overview') {
      return (
        <div className="space-y-4">
          <div className="rounded-[2rem] border border-white/10 bg-linear-to-br from-cyan-500/15 via-blue-500/10 to-emerald-500/10 p-5 md:p-6">
            <p className="text-xs font-black uppercase tracking-[0.25em] text-cyan-200">Référence de formation interne</p>
            <h3 className="mt-2 max-w-5xl text-2xl font-black leading-tight text-white md:text-4xl">
              Du besoin client à une mise en service maîtrisée.
            </h3>
            <p className="mt-3 max-w-5xl text-sm leading-6 text-slate-200 md:text-base">
              Standardiser la qualification, la proposition et la livraison des solutions de téléphonie hébergée,
              de câblage structuré et de fibre optique vendues par BZ Telecom.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {[
              ['2', 'volets de vente', 'Téléphonie et infrastructure physique'],
              ['6 + 6', 'étapes terrain', 'De la prise de besoin à la fermeture'],
              ['12', 'termes vulgarisés', 'Un langage commun pour les ventes'],
              ['5', 'produits rattachés', 'SMS, rapports, EVA, urgence et transcription'],
            ].map(([value, label, detail]) => (
              <div key={label} className="rounded-3xl border border-white/10 bg-white/[0.055] p-4">
                <p className="text-3xl font-black text-white">{value}</p>
                <p className="mt-1 text-xs font-black uppercase tracking-wide text-cyan-200">{label}</p>
                <p className="mt-1 text-xs text-slate-400">{detail}</p>
              </div>
            ))}
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            <SectionCard title="Vendre avec méthode" icon={ClipboardCheck} tone="cyan">
              <p>Qualifier le contexte et les douleurs, confirmer la faisabilité, présenter une architecture compréhensible et planifier une transition sans surprise.</p>
            </SectionCard>
            <SectionCard title="Vulgariser sans jargon" icon={BookOpenCheck} tone="violet">
              <p>Expliquer clairement VoIP, PBX, SIP, QoS, portabilité, IVR et files d’appel afin que le client décide en confiance.</p>
            </SectionCard>
            <SectionCard title="Livrer avec preuve" icon={ShieldCheck} tone="emerald">
              <p>Tester, documenter, étiqueter, certifier lorsque requis et fermer le projet avec une validation finale du client.</p>
            </SectionCard>
          </div>
        </div>
      );
    }

    if (activeTab === 'telephony') {
      return (
        <div className="space-y-4">
          <SectionCard title="Processus de vente — Téléphonie hébergée" icon={PhoneCall} tone="cyan">
            <p className="mb-4 text-base font-semibold text-white">Vendre une solution fiable, évolutive et simple à gérer pour moderniser les communications du client.</p>
            <StepGrid steps={telephonySalesSteps} />
          </SectionCard>
          <div className="grid gap-4 lg:grid-cols-2">
            <SectionCard title="Produits inclus dans la conversation" icon={PackageCheck} tone="emerald"><BulletList items={telephonyProducts} /></SectionCard>
            <SectionCard title="Contrôle réseau avant proposition" icon={Network} tone="amber"><BulletList items={networkChecks} /></SectionCard>
          </div>
        </div>
      );
    }

    if (activeTab === 'cabling') {
      return (
        <div className="space-y-4">
          <SectionCard title="Processus de vente — Câblage structuré et fibre" icon={Cable} tone="violet">
            <p className="mb-4 text-base font-semibold text-white">Concevoir une infrastructure propre, certifiable et prête pour la croissance réseau, Wi-Fi, téléphonie, caméras et équipements critiques.</p>
            <StepGrid steps={cablingSteps} />
          </SectionCard>
          <div className="grid gap-4 lg:grid-cols-2">
            <SectionCard title="Portée vendue" icon={ListChecks} tone="emerald"><BulletList items={cablingProducts} /></SectionCard>
            <SectionCard title="Risques à fermer avant la soumission" icon={ShieldCheck} tone="amber"><BulletList items={cablingRisks} /></SectionCard>
          </div>
        </div>
      );
    }

    if (activeTab === 'wiki') {
      return (
        <div className="space-y-4">
          <div className="grid gap-4 lg:grid-cols-[1.15fr_0.85fr]">
            <SectionCard title="La téléphonie hébergée, simplement" icon={CloudCog} tone="cyan">
              <p>Le système téléphonique d’entreprise est hébergé dans une infrastructure sécurisée plutôt que dans les locaux. Téléphones IP, applications mobiles et softphones s’y connectent par Internet.</p>
              <p className="mt-3">Les employés répondent du bureau, de la maison ou en mobilité tout en conservant leur numéro professionnel et les mêmes fonctions.</p>
            </SectionCard>
            <SectionCard title="Avantages à expliquer" icon={Sparkles} tone="emerald">
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
                {hostedBenefits.map(([title, body]) => <div key={title} className="rounded-2xl border border-white/10 bg-slate-950/40 p-3"><p className="font-black text-white">{title}</p><p className="mt-1 text-xs leading-5 text-slate-300">{body}</p></div>)}
              </div>
            </SectionCard>
          </div>
          <SectionCard title="Fonctionnalités populaires" icon={PhoneCall} tone="slate">
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3"><BulletList items={popularFeatures} /></div>
          </SectionCard>
          <SectionCard title="Lexique vulgarisé" icon={BookOpenCheck} tone="violet">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {glossary.map(([term, definition]) => <div key={term} className="rounded-2xl border border-white/10 bg-slate-950/45 p-4"><h4 className="font-black text-white">{term}</h4><p className="mt-1 text-xs leading-5 text-slate-300">{definition}</p></div>)}
            </div>
          </SectionCard>
        </div>
      );
    }

    if (activeTab === 'offer') {
      return (
        <div className="space-y-4">
          <div className="rounded-[2rem] border border-cyan-400/20 bg-linear-to-br from-cyan-500/15 via-slate-900 to-blue-500/10 p-5 md:p-6">
            <p className="text-xs font-black uppercase tracking-[0.25em] text-cyan-200">Positionnement client</p>
            <h3 className="mt-2 text-2xl font-black text-white md:text-4xl">Une plateforme moderne. Un accompagnement local. Une solution clé en main.</h3>
            <p className="mt-3 max-w-5xl text-sm leading-6 text-slate-200">BZ Telecom analyse, configure, migre les numéros, installe, forme et soutient le client au quotidien. L’expertise télécom, TI et IA est réunie dans une offre évolutive.</p>
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            <SectionCard title="Clé en main" icon={ClipboardCheck} tone="cyan"><BulletList items={turnkeyServices} /></SectionCard>
            <SectionCard title="Plateforme entreprise" icon={CloudCog} tone="violet"><BulletList items={platformFeatures} /></SectionCard>
            <SectionCard title="Téléphonie intelligente" icon={BrainCircuit} tone="emerald"><BulletList items={aiFeatures} /></SectionCard>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <SectionCard title="Idéale pour" icon={Users} tone="slate"><div className="flex flex-wrap gap-2">{idealFor.map((item) => <span key={item} className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-bold text-slate-200">{item}</span>)}</div></SectionCard>
            <SectionCard title="Fiabilité et sécurité" icon={ShieldCheck} tone="amber"><BulletList items={['Infrastructure redondante', 'Sauvegardes régulières', 'Communications sécurisées', 'Surveillance proactive', 'Mises à jour effectuées par les équipes BZ Telecom']} /></SectionCard>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="rounded-[2rem] border border-white/10 bg-linear-to-br from-emerald-500/15 via-slate-900 to-cyan-500/10 p-5 md:p-6">
          <p className="text-xs font-black uppercase tracking-[0.25em] text-emerald-200">Élargir la valeur par client</p>
          <h3 className="mt-2 text-2xl font-black text-white md:text-4xl">Cinq produits complémentaires autour de la communication.</h3>
          <p className="mt-3 max-w-4xl text-sm leading-6 text-slate-200">Ils répondent à des besoins distincts : collaboration SMS, pilotage des files, intelligence des appels, continuité d’urgence et traitement accéléré des messages vocaux.</p>
        </div>
        <div className="grid gap-4 xl:grid-cols-2">
          {relatedProducts.map((product) => {
            const Icon = product.icon;
            return (
              <SectionCard key={product.title} title={product.title} icon={Icon} tone="slate">
                <p className="mb-3 font-semibold text-white">{product.summary}</p>
                <div className="grid gap-2 sm:grid-cols-2"><BulletList items={product.features} /></div>
              </SectionCard>
            );
          })}
        </div>
      </div>
    );
  }, [activeTab]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/88 p-2 backdrop-blur-md md:p-4" role="presentation">
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.2 }}
            className="flex max-h-[94dvh] w-full max-w-[min(1600px,96vw)] flex-col overflow-hidden rounded-[1.75rem] border border-white/10 bg-[#050811] shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="telecom-training-title"
          >
            <header className="relative shrink-0 overflow-hidden border-b border-white/10 px-5 py-4 md:px-7 md:py-5">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_0%,rgba(34,211,238,0.22),transparent_36%),radial-gradient(circle_at_88%_15%,rgba(16,185,129,0.16),transparent_30%)]" />
              <button type="button" onClick={onClose} className="absolute right-3 top-3 z-10 rounded-full border border-white/10 bg-white/10 p-2 text-white transition hover:bg-white/20" aria-label="Fermer la formation">
                <X className="h-5 w-5" />
              </button>
              <div className="relative flex items-start gap-4 pr-10">
                <div className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-cyan-300/20 bg-cyan-400/10 text-cyan-100 sm:flex"><PhoneCall className="h-6 w-6" /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-cyan-200"><span>PRIsME · BZ Telecom</span><span className="rounded-full border border-emerald-300/20 bg-emerald-400/10 px-2 py-1 text-[10px] tracking-normal text-emerald-100">Formation interne</span></div>
                  <h2 id="telecom-training-title" className="mt-1 text-2xl font-black leading-tight text-white md:text-3xl">Formation téléphonie BZ Telecom</h2>
                  <p className="mt-1 max-w-5xl text-xs leading-5 text-slate-300 md:text-sm">Téléphonie hébergée, câblage structuré, fibre optique, wiki et produits rattachés.</p>
                </div>
                <a href={SOURCE_URL} target="_blank" rel="noreferrer" className="mr-8 hidden shrink-0 items-center gap-2 rounded-full border border-cyan-300/25 bg-cyan-400/10 px-4 py-2 text-xs font-black text-cyan-50 transition hover:bg-cyan-400/20 lg:inline-flex">
                  <FileText className="h-4 w-4" /> Ouvrir le PDF source <ArrowUpRight className="h-4 w-4" />
                </a>
              </div>
            </header>

            <div className="flex shrink-0 gap-2 overflow-x-auto border-b border-white/10 bg-slate-950/80 px-3 py-2.5 md:px-5" role="tablist" aria-label="Sections de la formation">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.id;
                return (
                  <button key={tab.id} type="button" role="tab" aria-selected={active} onClick={() => setActiveTab(tab.id)} className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-3.5 py-2 text-xs font-black transition md:text-sm ${active ? 'border-cyan-300/40 bg-cyan-400/15 text-white' : 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10'}`}>
                    <Icon className="h-4 w-4" />{tab.label}
                  </button>
                );
              })}
            </div>

            <main data-telecom-training-scroll className="min-h-0 flex-1 overflow-y-auto px-4 py-4 md:px-6 md:py-5">{content}</main>

            <footer className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-white/10 bg-slate-950/90 px-4 py-2.5 text-[11px] text-slate-400 md:px-6">
              <span className="inline-flex items-center gap-2"><FileText className="h-3.5 w-3.5 text-cyan-300" />Source : {SOURCE_FILE} · 11 pages</span>
              <a href={SOURCE_URL} download={SOURCE_FILE} className="inline-flex items-center gap-1.5 font-bold text-cyan-200 transition hover:text-white"><FileDown className="h-3.5 w-3.5" />Télécharger la référence</a>
            </footer>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
